import { initPaginator, initProductList } from "./main";
import { buildQueryParameters, QueryParameter, getUrlWithoutQueryString, getQueryParameters, getBaseUrl, getCurrentLanguageId } from "./common";
import HttpClient from "./http-client";
import LoadingIndication, { ComponentLoadingIndication } from "./loading-indication";

export class Action {
    type: string;
    url: string;
    components: Component[];
    successAction: () => void;
}

export class JsonAction {
    type: string;
    url: string;
}

export class Component {
    id?: string;
    inputComponent?: string;
    outputComponent: string;

    constructor(output: string, id: string = null) {
        this.outputComponent = output;
        this.id = id;
    }
}

export class AjaxDispatcher {
    dispatchPostAction(action: Action, data: FormData) {
        this.displayLoadingOverlay(action.components);
        new HttpClient().fetch(action.url,
            {
                method: "POST",
                headers: {
                    requestUrl: window.location.href
                },
                body: data
            })
            .then(response => this.onActionSuccess(response, action))
            .catch(error => this.onActionError(error));
    }

    dispatchGetAction(action: Action, useLoadingOverlay = true) {
        useLoadingOverlay && this.displayLoadingOverlay(action.components);

        new HttpClient().fetch(action.url, { method: "GET" })
            .then(response => this.onActionSuccess(response, action))
            .catch(error => this.onActionError(error));
    }

    private displayLoadingOverlay(components: Component[]){
        components && components.forEach(component => 
            new ComponentLoadingIndication(component).show()
        );
    }

    private onActionSuccess(response: Response, action: Action) {
        if (response.redirected) {
            this.redirect(response);
            return;
        }
        
        response.text().then(html => {
            const serverDom = new DOMParser().parseFromString(html, "text/html");
            for (let i = 0; i < action.components.length; i++) {
                const item = action.components[i];
                
                if (item.id) {
                    const rendered = serverDom.getElementById(item.id);
                    const targetElement = document.getElementById(item.id);
                    targetElement.innerHTML = rendered.innerHTML;
                    LoadingIndication.hide(targetElement as HTMLElement);
                    targetElement.replaceWith(rendered);
                }
                else {
                    //If no id, there has to be only one component of type
                    const output = item.outputComponent;
                    const input = item.inputComponent ? item.inputComponent : item.outputComponent;
                    const rendered = serverDom.querySelector(`[data-component="${output}"]`);
                    const targetComponents = window.document.querySelectorAll(`[data-component="${input}"]`);
                    
                    if (rendered !== null) {
                        targetComponents.forEach(element => {
                            LoadingIndication.hide(element as HTMLElement);
                            element.replaceWith(rendered);
                        });
                    }
                }
            }

            if (action.successAction) {
                action.successAction();
            }
        });
    }

    private redirect(response: Response) {
        window.location.href = response.url;
    }

    private onActionError(error: any) {
        window.console.error(error);
    }
}

export class ActionCreator {
    readonly searchActionType = "SearchAction";

    searchAction(query: string): Action {
        const action = new Action();
        const components = [new Component('SearchBarResult')];
        const url = getBaseUrl() + '?q=' + query + "&ajax=true" + this.toQueryParameter(components) + '&langId=' + getCurrentLanguageId();

        action.components = components;
        action.url = url;
        action.type = this.searchActionType;

        return action;
    }

    toQueryParameter(components: Component[]): string {
        return `&viewComponent=${this.getComponentQueryParamValue(components)}`;
    }

    getComponentQueryParamValue(components: Component[]): string{
        let parameter = '';
        for (let i = 0; i < components.length; i++) {
            const component = components[i];
            if (component.id) {
                parameter += `${component.outputComponent}_${component.id};`;
            } else {
                parameter += `${component.outputComponent};`;
            }
        }
        return parameter;
    }

    reloadProductListAction(queryString: string, viewComponentName = 'ProductList', componentId: string = 'Catalog'): Action {
        const action = new Action();
        const components = componentId ? [new Component(viewComponentName, componentId)] : [new Component(viewComponentName)];
        const url = queryString ? getUrlWithoutQueryString() + '?' + queryString : getUrlWithoutQueryString();
        const urlWithViewComponent = `${url}${queryString ? '&' : '?'}viewComponent=${this.getComponentQueryParamValue(components)}`;

        action.components = components;
        action.type = 'FilterAction';
        action.url = urlWithViewComponent;
        action.successAction = () => {
            initPaginator();
            initProductList();
        }
        
        const newUrlState = { Title: document.title, Url: url };
        window.history.pushState(newUrlState, document.title, url);

        return action;
    }

    reloadOrders(queryParameters: QueryParameter[]): Action {
        const componentName = 'Orders';
        const action = new Action();

        const queryParams = [{key: 'viewComponent', value: componentName}, ...queryParameters];

        action.components = [new Component(componentName)];
        const queryString = buildQueryParameters(queryParams);
        action.url = queryString ? getUrlWithoutQueryString() + '?' + queryString : getUrlWithoutQueryString();

        return action;
    }

    reloadComponent(componentName: string, queryParameters: QueryParameter[] = []): Action {
        const action = new Action();

        const queryParams = [{key: 'viewComponent', value: componentName}, ...queryParameters];
        action.components = [new Component(componentName)];

        if (this.containsUrlQuestionMark()) {
            action.url = this.appendQueryParametersToExisting(queryParams);
        } else {
            action.url = this.appendQueryParameters(queryParams);
        }

        return action;
    }

    reloadComponents(componentNames: string[], url: string = null): Action {
        const action = new Action();
        
        action.components = componentNames.map(c => new Component(c));
        const componentParameters = componentNames.join(';');

        const queryParameters = getQueryParameters()
            .filter(param => param.key !== 'viewComponent' && param.key.indexOf('calc-') !== 0);
        queryParameters.push({ key: 'viewComponent', value: componentParameters });

        action.url = `${url ?? getUrlWithoutQueryString()}?${buildQueryParameters(queryParameters)}`;
        return action;
    }

    loadComponentsAjax(componentNames: string[], url: string = null): Action {
        const action = new Action();
        
        action.components = componentNames.map(c => new Component(c));
        const componentParameters = componentNames.join(';');

        const queryParameters = getQueryParameters()
            .filter(param => param.key !== 'viewComponent');
        queryParameters.push({ key: 'viewComponent', value: componentParameters });
        queryParameters.push({ key: 'ajax', value: 'true' });

        action.url = `${url ?? getUrlWithoutQueryString()}?${buildQueryParameters(queryParameters)}`;
        return action;
    }

    private containsUrlQuestionMark(): boolean {
        return window.location.href.split('?').length > 1;
    }

    private appendQueryParametersToExisting(queryParameters: QueryParameter[]): string {
        const parameters = buildQueryParameters(queryParameters);
        return `${window.location.href}${parameters ? '&' : null}${parameters}`;
    }

    private appendQueryParameters(queryParameters: QueryParameter[]): string {
        const parameters = buildQueryParameters(queryParameters);
        return `${window.location.href}${parameters ? '?' : null}${parameters}`;
    }

}
